import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Users, MessageSquare, Loader2, Plus, Flag, ArrowLeft, Send,
  Clock, Trash2, X, ImagePlus, Heart, Flame, TrendingUp, Calendar,
} from "lucide-react";

const imageSrc = (urlPath) => {
  if (!urlPath) return null;
  const token = localStorage.getItem("pm_token") || "";
  return `${API}${urlPath}?auth=${encodeURIComponent(token)}`;
};

const SUBJECT_ACCENTS = {
  bio:        "#4f7942",
  biochem:    "#2d6a4f",
  chem:       "#3f7fbf",
  ochem:      "#8f5b2a",
  physics:    "#c98a3b",
  physiology: "#b85c5c",
  psych_soc:  "#7a5c9e",
  cars:       "#1d3b4a",
  general:    "#6a7570",
};
const POST_TYPE_LABEL = {
  question:   "Question",
  analogy:    "Analogy",
  discussion: "Discussion",
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function SubjectPill({ subject, size = "sm" }) {
  const accent = SUBJECT_ACCENTS[subject] || SUBJECT_ACCENTS.general;
  const label = {
    bio: "Biology", biochem: "Biochem", chem: "Chemistry", ochem: "O-Chem",
    physics: "Physics", physiology: "Physiology", psych_soc: "Psych/Soc",
    cars: "CARS", general: "General",
  }[subject] || subject;
  return (
    <span
      className={`inline-block rounded-full font-semibold ${size === "sm" ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`}
      style={{ background: `${accent}1f`, color: accent, letterSpacing: "0.04em", textTransform: "uppercase" }}
      data-testid="community-subject-pill"
    >
      {label}
    </span>
  );
}

function NewPostModal({ subjects, onClose, onCreated }) {
  const [subject, setSubject] = useState("bio");
  const [type, setType] = useState("question");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);      // the File blob
  const [imagePreview, setImagePreview] = useState(null); // local objectURL
  const [imageFileId, setImageFileId] = useState(null);  // once uploaded
  const [uploading, setUploading] = useState(false);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be ≤5MB"); return; }
    if (!/^image\/(png|jpeg|jpg|webp|gif)$/.test(f.type)) {
      toast.error("Image type not supported (png, jpeg, webp, gif)"); return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
    setImageFileId(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/community-board/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageFileId(r.data.file_id);
      toast.success("Image attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setImageFile(null);
      setImagePreview(null);
    } finally { setUploading(false); }
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null); setImagePreview(null); setImageFileId(null);
  };

  const submit = async () => {
    if (title.trim().length < 3) { toast.error("Title too short"); return; }
    if (body.trim().length < 1) { toast.error("Add some body text"); return; }
    if (uploading) { toast.error("Image still uploading…"); return; }
    setSaving(true);
    try {
      const payload = { subject, type, title: title.trim(), body: body.trim() };
      if (imageFileId) payload.image_file_id = imageFileId;
      const r = await api.post("/community-board/posts", payload);
      toast.success("Posted to the community");
      onCreated?.(r.data.post);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post. Try again.");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="community-new-post-modal"
    >
      <div
        className="pm-card w-full max-w-xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display font-bold text-xl">New community post</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>Free to post — no credits used.</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="community-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Subject</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="pm-input w-full mt-1"
              data-testid="community-new-subject"
            >
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="pm-input w-full mt-1"
              data-testid="community-new-type"
            >
              <option value="question">Question</option>
              <option value="analogy">Analogy to share</option>
              <option value="discussion">Discussion</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Why does the citric acid cycle produce 3 NADH?"
            className="pm-input w-full mt-1"
            maxLength={160}
            data-testid="community-new-title"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Share the question, analogy, or discussion…"
            className="pm-input w-full mt-1 min-h-[160px]"
            maxLength={6000}
            data-testid="community-new-body"
          />
          <div className="text-[10px] text-right mt-1" style={{ color: "var(--text-muted)" }}>{body.length}/6000</div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Image (optional)</label>
          {imagePreview ? (
            <div className="mt-2 relative rounded-xl overflow-hidden border" style={{ borderColor: "var(--border)" }}>
              <img src={imagePreview} alt="attachment preview" className="w-full max-h-64 object-contain bg-black/5" data-testid="community-image-preview" />
              <button
                onClick={clearImage}
                className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"
                data-testid="community-image-remove"
                title="Remove image"
              >
                <X size={14} />
              </button>
              {uploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-xs">
                  <Loader2 size={16} className="animate-spin mr-1" /> Uploading…
                </div>
              )}
            </div>
          ) : (
            <label
              className="mt-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed cursor-pointer hover:bg-white/5"
              style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
              data-testid="community-image-upload-label"
            >
              <ImagePlus size={14} />
              <span className="text-sm">Attach an image (png, jpeg, webp — up to 5MB)</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={pickFile}
                data-testid="community-image-input"
              />
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="pm-btn-secondary" data-testid="community-cancel-btn">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="pm-btn-primary inline-flex items-center gap-2"
            data-testid="community-submit-post"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Posting…</> : <><Send size={14} /> Post</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportModal({ target, onClose }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const path = target.type === "post"
        ? `/community-board/posts/${target.id}/report`
        : `/community-board/replies/${target.id}/report`;
      await api.post(path, { reason: reason.trim() });
      toast.success("Thanks — our team will review this.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send report");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="community-report-modal"
    >
      <div className="pm-card w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display font-bold text-lg">Report content</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>Tell us why so moderators can review quickly.</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5"><X size={16} /></button>
        </div>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional) — e.g. spam, offensive, misinformation"
          className="pm-input w-full min-h-[100px]"
          maxLength={500}
          data-testid="community-report-reason"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="pm-btn-secondary">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="pm-btn-primary inline-flex items-center gap-2"
            data-testid="community-report-submit"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Flag size={14} /> Report</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, onOpen, onReport, onDelete, onVote, voted, currentUserId }) {
  const isMine = post.author_id === currentUserId;
  const accent = SUBJECT_ACCENTS[post.subject] || SUBJECT_ACCENTS.general;
  return (
    <div
      className="pm-card p-5 hover:border-[var(--primary)] transition-colors cursor-pointer"
      onClick={() => onOpen(post)}
      data-testid={`community-post-card-${post.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <SubjectPill subject={post.subject} />
        <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>
          {POST_TYPE_LABEL[post.type] || "Question"}
        </span>
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>·</span>
        <span className="text-xs inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
          <Clock size={11} /> {timeAgo(post.created_at)}
        </span>
      </div>
      <h3 className="font-display font-bold text-lg mb-1 leading-tight" data-testid={`community-post-title-${post.id}`}>
        {post.title}
      </h3>
      {post.image_url && (
        <div className="mb-3 rounded-lg overflow-hidden" style={{ background: `${accent}0f` }}>
          <img
            src={imageSrc(post.image_url)}
            alt=""
            className="w-full max-h-52 object-cover"
            loading="lazy"
            data-testid={`community-post-image-${post.id}`}
          />
        </div>
      )}
      <p className="text-sm line-clamp-2 mb-3" style={{ color: "var(--text-secondary)" }}>
        {post.body}
      </p>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
          by <span className="font-semibold" style={{ color: "var(--text)" }}>{post.author_name}</span>
        </div>
        <div className="flex items-center gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
          <button
            onClick={(e) => { e.stopPropagation(); onVote({ type: "post", id: post.id }); }}
            className="inline-flex items-center gap-1 hover:text-[var(--primary)]"
            style={{ color: voted ? "#c95e5e" : undefined }}
            data-testid={`community-vote-post-${post.id}`}
            title={voted ? "Remove upvote" : "Upvote"}
          >
            <Heart size={12} fill={voted ? "#c95e5e" : "none"} /> {post.upvotes || 0}
          </button>
          <span className="inline-flex items-center gap-1"><MessageSquare size={12} /> {post.reply_count}</span>
          {isMine && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(post); }}
              className="inline-flex items-center gap-1 hover:text-[var(--primary)]"
              data-testid={`community-delete-post-${post.id}`}
            >
              <Trash2 size={12} /> Delete
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onReport({ type: "post", id: post.id }); }}
            className="inline-flex items-center gap-1 hover:text-[#c95e5e]"
            data-testid={`community-report-post-${post.id}`}
          >
            <Flag size={12} /> Report
          </button>
        </div>
      </div>
    </div>
  );
}

function PostDetail({ postId, onBack, onReport, onVote, votedPosts, votedReplies, currentUserId }) {
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/community-board/posts/${postId}`);
      setPost(r.data.post);
      setReplies(r.data.replies || []);
    } catch (e) {
      toast.error("Post not found");
      onBack();
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [postId]);

  const submitReply = async () => {
    if (replyText.trim().length < 1) return;
    setSending(true);
    try {
      const r = await api.post(`/community-board/posts/${postId}/replies`, { body: replyText.trim() });
      setReplies((prev) => [...prev, r.data.reply]);
      setPost((prev) => prev ? { ...prev, reply_count: (prev.reply_count || 0) + 1 } : prev);
      setReplyText("");
      toast.success("Reply posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post reply");
    } finally { setSending(false); }
  };

  const deleteReply = async (reply) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/community-board/replies/${reply.id}`);
      setReplies((prev) => prev.filter((x) => x.id !== reply.id));
      setPost((prev) => prev ? { ...prev, reply_count: Math.max(0, (prev.reply_count || 1) - 1) } : prev);
    } catch (e) {
      toast.error("Couldn't delete");
    }
  };

  // Local upvote toggle — we delegate the API call to the parent so vote state
  // stays in sync when the user returns to the board.
  const togglePostVote = async () => {
    const prevVoted = votedPosts?.has(post.id);
    const next = await onVote({ type: "post", id: post.id });
    if (next) {
      setPost((p) => p ? { ...p, upvotes: next.upvotes } : p);
    } else {
      // optimistic rollback no-op if API failed
      void prevVoted;
    }
  };

  const toggleReplyVote = async (reply) => {
    const next = await onVote({ type: "reply", id: reply.id });
    if (next) {
      setReplies((prev) => prev.map((r) => r.id === reply.id ? { ...r, upvotes: next.upvotes } : r));
    }
  };

  if (loading) {
    return <div className="p-10 text-center"><Loader2 className="inline animate-spin" /></div>;
  }
  if (!post) return null;

  return (
    <div className="space-y-5" data-testid="community-post-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: "var(--text-muted)" }} data-testid="community-back-btn">
        <ArrowLeft size={14} /> Back to board
      </button>

      <article className="pm-card p-6">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <SubjectPill subject={post.subject} size="md" />
          <span className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>
            {POST_TYPE_LABEL[post.type] || "Question"}
          </span>
        </div>
        <h1 className="font-display font-bold text-2xl md:text-3xl mb-2 leading-tight">{post.title}</h1>
        <div className="text-xs mb-5 flex items-center gap-2 flex-wrap" style={{ color: "var(--text-muted)" }}>
          <span>by <strong style={{ color: "var(--text)" }}>{post.author_name}</strong></span>
          <span>·</span>
          <span>{timeAgo(post.created_at)}</span>
          <span className="ml-auto flex items-center gap-3">
            <button
              onClick={togglePostVote}
              className="inline-flex items-center gap-1 hover:text-[var(--primary)]"
              style={{ color: votedPosts?.has(post.id) ? "#c95e5e" : undefined }}
              data-testid="community-detail-vote-post"
              title={votedPosts?.has(post.id) ? "Remove upvote" : "Upvote"}
            >
              <Heart size={12} fill={votedPosts?.has(post.id) ? "#c95e5e" : "none"} /> {post.upvotes || 0}
            </button>
            <button
              onClick={() => onReport({ type: "post", id: post.id })}
              className="inline-flex items-center gap-1 hover:text-[#c95e5e]"
              data-testid="community-detail-report-post"
            >
              <Flag size={12} /> Report
            </button>
          </span>
        </div>
        {post.image_url && (
          <div className="mb-5 rounded-xl overflow-hidden border" style={{ borderColor: "var(--border)" }}>
            <img
              src={imageSrc(post.image_url)}
              alt=""
              className="w-full max-h-[480px] object-contain bg-black/5"
              data-testid="community-detail-image"
            />
          </div>
        )}
        <div className="whitespace-pre-wrap text-[15px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          {post.body}
        </div>
      </article>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} style={{ color: "var(--text-muted)" }} />
          <div className="font-display font-bold text-lg">{replies.length} {replies.length === 1 ? "reply" : "replies"}</div>
        </div>

        {replies.map((r) => {
          const isMine = r.author_id === currentUserId;
          return (
            <div key={r.id} className="pm-card p-4" data-testid={`community-reply-${r.id}`}>
              <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--text-muted)" }}>
                <strong style={{ color: "var(--text)" }}>{r.author_name}</strong>
                <span>·</span>
                <span>{timeAgo(r.created_at)}</span>
                <span className="ml-auto flex items-center gap-3">
                  <button
                    onClick={() => toggleReplyVote(r)}
                    className="inline-flex items-center gap-1 hover:text-[var(--primary)]"
                    style={{ color: votedReplies?.has(r.id) ? "#c95e5e" : undefined }}
                    data-testid={`community-vote-reply-${r.id}`}
                    title={votedReplies?.has(r.id) ? "Remove upvote" : "Upvote"}
                  >
                    <Heart size={11} fill={votedReplies?.has(r.id) ? "#c95e5e" : "none"} /> {r.upvotes || 0}
                  </button>
                  {isMine && (
                    <button
                      onClick={() => deleteReply(r)}
                      className="inline-flex items-center gap-1 hover:text-[var(--primary)]"
                      data-testid={`community-delete-reply-${r.id}`}
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  )}
                  <button
                    onClick={() => onReport({ type: "reply", id: r.id })}
                    className="inline-flex items-center gap-1 hover:text-[#c95e5e]"
                    data-testid={`community-report-reply-${r.id}`}
                  >
                    <Flag size={11} /> Report
                  </button>
                </span>
              </div>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{r.body}</div>
            </div>
          );
        })}

        <div className="pm-card p-4" data-testid="community-reply-composer">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Add a reply… (free, no credits used)"
            className="pm-input w-full min-h-[100px]"
            maxLength={4000}
            data-testid="community-reply-textarea"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{replyText.length}/4000</span>
            <button
              onClick={submitReply}
              disabled={sending || replyText.trim().length < 1}
              className="pm-btn-primary inline-flex items-center gap-2"
              data-testid="community-submit-reply"
            >
              {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Post reply</>}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function CommunityBoard() {
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState("all");
  const [sort, setSort] = useState("new");
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // { type: "post"|"reply", id }
  const [openPostId, setOpenPostId] = useState(null);
  const [votedPosts, setVotedPosts] = useState(new Set());
  const [votedReplies, setVotedReplies] = useState(new Set());

  const loadSubjects = async () => {
    try {
      const r = await api.get("/community-board/subjects");
      setSubjects(r.data.subjects || []);
    } catch { /* ignore */ }
  };

  const loadMyVotes = async () => {
    try {
      const r = await api.get("/community-board/my-votes");
      setVotedPosts(new Set(r.data.posts || []));
      setVotedReplies(new Set(r.data.replies || []));
    } catch { /* ignore */ }
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      const params = { limit: 50, sort };
      if (subject !== "all") params.subject = subject;
      const r = await api.get("/community-board/posts", { params });
      setPosts(r.data.posts || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Couldn't load board");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadSubjects(); loadMyVotes(); }, []);
  useEffect(() => { loadPosts(); /* eslint-disable-next-line */ }, [subject, sort]);

  const subjectTabs = useMemo(() => [
    { id: "all", label: "All", post_count: total },
    ...subjects,
  ], [subjects, total]);

  const toggleVote = async ({ type, id }) => {
    try {
      const path = type === "post"
        ? `/community-board/posts/${id}/vote`
        : `/community-board/replies/${id}/vote`;
      const r = await api.post(path);
      const { upvotes, voted } = r.data;
      if (type === "post") {
        setVotedPosts((prev) => {
          const next = new Set(prev);
          if (voted) next.add(id); else next.delete(id);
          return next;
        });
        setPosts((prev) => prev.map((p) => p.id === id ? { ...p, upvotes } : p));
      } else {
        setVotedReplies((prev) => {
          const next = new Set(prev);
          if (voted) next.add(id); else next.delete(id);
          return next;
        });
      }
      return { upvotes, voted };
    } catch (e) {
      toast.error("Couldn't update vote");
      return null;
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm("Delete this post? Replies will also be removed.")) return;
    try {
      await api.delete(`/community-board/posts/${post.id}`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error("Couldn't delete post");
    }
  };

  if (openPostId) {
    return (
      <div className="max-w-3xl mx-auto space-y-6 py-6" data-testid="community-board-detail">
        <PostDetail
          postId={openPostId}
          currentUserId={user?.id}
          onBack={() => { setOpenPostId(null); loadPosts(); loadSubjects(); }}
          onReport={(t) => setReportTarget(t)}
          onVote={toggleVote}
          votedPosts={votedPosts}
          votedReplies={votedReplies}
        />
        {reportTarget && <ReportModal target={reportTarget} onClose={() => setReportTarget(null)} />}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 py-6" data-testid="community-board">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl flex items-center gap-2">
            <Users size={28} style={{ color: "var(--primary)" }} /> Community Board
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Post study questions, share analogies, help each other out. Free — no credits used.
          </p>
        </div>
        <button
          onClick={() => setShowCompose(true)}
          className="pm-btn-primary inline-flex items-center gap-2"
          data-testid="community-new-post-btn"
        >
          <Plus size={14} /> New post
        </button>
      </header>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-2 overflow-x-auto pb-1 flex-1" data-testid="community-subject-tabs">
          {subjectTabs.map((s) => {
            const active = subject === s.id;
            const accent = s.id === "all" ? "var(--primary)" : (SUBJECT_ACCENTS[s.id] || "var(--primary)");
            return (
              <button
                key={s.id}
                onClick={() => setSubject(s.id)}
                className="pm-badge inline-flex items-center gap-1.5 whitespace-nowrap"
                style={{
                  background: active ? accent : "var(--muted)",
                  color: active ? "white" : "var(--text)",
                  padding: "6px 12px",
                  cursor: "pointer",
                }}
                data-testid={`community-tab-${s.id}`}
              >
                {s.label}
                <span
                  className="text-[10px] rounded-full px-1.5"
                  style={{
                    background: active ? "rgba(255,255,255,0.25)" : "var(--border)",
                    color: active ? "white" : "var(--text-muted)",
                  }}
                >
                  {s.post_count || 0}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1 shrink-0" data-testid="community-sort-group">
          {[
            { id: "hot", label: "Hot", icon: Flame },
            { id: "new", label: "New", icon: Calendar },
            { id: "top", label: "Top", icon: TrendingUp },
          ].map((o) => {
            const Icon = o.icon;
            const active = sort === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setSort(o.id)}
                className="pm-badge inline-flex items-center gap-1 whitespace-nowrap"
                style={{
                  background: active ? "var(--primary)" : "var(--muted)",
                  color: active ? "white" : "var(--text-muted)",
                  padding: "6px 10px",
                  cursor: "pointer",
                }}
                data-testid={`community-sort-${o.id}`}
                title={`Sort by ${o.label}`}
              >
                <Icon size={11} /> {o.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3" data-testid="community-posts-list">
        {loading ? (
          <div className="pm-card p-10 text-center"><Loader2 className="inline animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="pm-card p-10 text-center" data-testid="community-empty-state">
            <Users size={32} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
            <div className="font-display font-bold text-lg mb-1">No posts yet in this subject</div>
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>Be the first to kick off the conversation.</div>
          </div>
        ) : (
          posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              currentUserId={user?.id}
              onOpen={(x) => setOpenPostId(x.id)}
              onReport={(t) => setReportTarget(t)}
              onDelete={deletePost}
              onVote={toggleVote}
              voted={votedPosts.has(p.id)}
            />
          ))
        )}
      </div>

      {showCompose && (
        <NewPostModal
          subjects={subjects.length ? subjects : [{ id: "bio", label: "Biology" }]}
          onClose={() => setShowCompose(false)}
          onCreated={() => { loadPosts(); loadSubjects(); }}
        />
      )}
      {reportTarget && <ReportModal target={reportTarget} onClose={() => setReportTarget(null)} />}
    </div>
  );
}
